import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import AccountDropdown from '../components/AccountDropdown';
import MoodLogoWithTooltip from '../components/MoodLogoWithTooltip';
import ProxyImage from '../components/ProxyImage';
import { verifySession, getCurrentUser, logout as authLogout, deleteAccount as authDeleteAccount } from '../services/auth';
import DeleteAccountModal from '../components/DeleteAccountModal';
import './TermsAndConditions.css';

const TermsAndConditions: React.FC = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState<boolean>(false);

  useEffect(() => {
    // Verificar modo oscuro
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    };
    checkDarkMode();

    // Verificar autenticación
    const checkAuth = async () => {
      try {
        const user = getCurrentUser();
        if (user) {
          const result = await verifySession();
          setIsAuthenticated(result.success || false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();

    // Observar cambios en el tema
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Sincronizar logout entre pestañas
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'logoutEvent' && e.newValue) {
        sessionStorage.setItem('logoutTimestamp', Date.now().toString());
        setIsAuthenticated(false);
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('oauthParams');
        document.documentElement.classList.remove('dark-theme');
        document.documentElement.classList.add('light-theme');
        localStorage.removeItem('theme');
        if (window.location.search) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        navigate('/');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  // Manejar logout
  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    try {
      setIsLoggingOut(true);
      sessionStorage.setItem('logoutTimestamp', Date.now().toString());
      sessionStorage.removeItem('oauthParams');
      
      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      await authLogout();
      
      document.documentElement.classList.remove('dark-theme');
      document.documentElement.classList.add('light-theme');
      localStorage.removeItem('theme');
      
      setIsAuthenticated(false);
      navigate('/');
    } catch (error) {
      setIsAuthenticated(false);
      navigate('/');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Manejar eliminar cuenta
  const handleDeleteAccount = async () => {
    if (isDeletingAccount) {
      return;
    }

    try {
      setIsDeletingAccount(true);
      
      const result = await authDeleteAccount();
      
      if (result.success) {
        localStorage.clear();
        sessionStorage.clear();
        window.history.replaceState(null, '', window.location.pathname);
        setIsAuthenticated(false);
        setShowDeleteAccountModal(false);
        navigate('/');
      }
    } catch (error) {
      // Error al eliminar cuenta
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <div className="terms-and-conditions-page">
      <div className="app-miniheader">
        <div className="miniheader-left">
          <a 
            href="/terminos-y-condiciones" 
            className="miniheader-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Términos y Condiciones
          </a>
        </div>
        <div className="miniheader-social">
          <a 
            href="https://smartcityexposantiagodelestero.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="miniheader-link"
          >
            smartcityexposantiagodelestero.com
          </a>
        </div>
      </div>
      <div className="app-header-bar terms-header-bar">
        <div className="header-logo">
          <a 
            href="/" 
            className="header-logo-link"
          >
            <ProxyImage 
              src={isDarkMode 
                ? "https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Logo-SCESDE26-Blanco-e1768488512211.png"
                : "https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png"
              } 
              alt="Smart City Expo Santiago del Estero" 
              className="header-logo-image"
            />
          </a>
        </div>
        {/* Logo Mood para mobile - fuera de header-actions */}
        <MoodLogoWithTooltip
          isDarkMode={isDarkMode}
          className="header-mood-logo header-mood-logo-mobile"
        />
        <div className="header-actions">
          <ThemeToggle />
          {isAuthenticated && (
            <AccountDropdown
              onLogout={handleLogout}
              onDeleteAccount={() => setShowDeleteAccountModal(true)}
              isLoggingOut={isLoggingOut}
            />
          )}
          {/* Logo Mood para tablet/desktop - dentro de header-actions */}
          <MoodLogoWithTooltip
            isDarkMode={isDarkMode}
            className="header-mood-logo header-mood-logo-desktop"
          />
        </div>
      </div>
      
      <div className="terms-container">
        <div className="terms-content">
          <h1 className="terms-title">TÉRMINOS Y CONDICIONES DE USO DEL PERFIL</h1>
          <p className="terms-subtitle">Smart City Expo Santiago del Estero 2026</p>
          
          <div className="terms-intro">
            <p>
              Al crear un perfil, cargar información y/o solicitar información para participar en Smart City Expo Santiago del Estero 2026 (en adelante, el "Evento"), el usuario (en adelante, el "Participante") declara haber leído, comprendido y aceptado los presentes Términos y Condiciones, así como la Política de Privacidad asociada.
            </p>
          </div>

          <div className="terms-section">
            <h2>1. Finalidad del perfil y de la información solicitada</h2>
            <p>La información proporcionada a través del perfil tiene como finalidad:</p>
            <ul>
              <li>Evaluar y gestionar solicitudes de participación en las distintas modalidades del Evento (expositores, partners, instituciones, gobiernos, speakers, rondas de negocios, catálogo, networking u otras).</li>
              <li>Facilitar la vinculación institucional, comercial y profesional entre los actores del ecosistema.</li>
              <li>Brindar información relevante sobre agendas, oportunidades de participación, contenidos y actividades del Evento.</li>
              <li>Generar estadísticas, reportes e indicadores de participación de manera agregada y no identificable.</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>2. Veracidad y actualización de la información</h2>
            <p>El Participante declara que toda la información cargada es veraz, completa y actualizada, y se compromete a mantenerla vigente.</p>
            <p>La organización del Evento se reserva el derecho de verificar, solicitar ampliaciones o rechazar información que resulte incompleta, inexacta o incompatible con los objetivos del Evento.</p>
          </div>

          <div className="terms-section">
            <h2>3. Uso de la información y confidencialidad</h2>
            <ul>
              <li>La información institucional, descriptiva y pública podrá ser utilizada por la organización para fines de evaluación, comunicación, vinculación y difusión relacionados con el Evento.</li>
              <li>La información marcada o considerada como confidencial no será divulgada públicamente sin consentimiento previo del Participante.</li>
              <li>La organización se compromete a aplicar medidas razonables de seguridad para la protección de los datos.</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>4. Protección de datos personales</h2>
            <p>Los datos personales serán tratados conforme a la normativa vigente en materia de protección de datos personales.</p>
            <p>El Participante consiente expresamente que sus datos sean utilizados para:</p>
            <ul>
              <li>Contacto institucional y comercial relacionado con el Evento.</li>
              <li>Envío de información, novedades y oportunidades vinculadas a Smart City Expo Santiago del Estero.</li>
              <li>Generación de perfiles de participación y segmentación temática.</li>
            </ul>
            <p>El Participante podrá ejercer en cualquier momento sus derechos de acceso, rectificación, actualización o supresión de sus datos.</p>
          </div>

          <div className="terms-section">
            <h2>5. Propiedad intelectual y uso de contenidos</h2>
            <ul>
              <li>El Participante declara ser titular o contar con los derechos necesarios sobre los textos, imágenes, logos, marcas, presentaciones y materiales cargados.</li>
              <li>Al cargar contenidos, el Participante autoriza a la organización del Evento a utilizarlos, reproducirlos y comunicarlos exclusivamente con fines institucionales, promocionales y de difusión del Evento, sin derecho a compensación económica.</li>
              <li>Esta autorización es no exclusiva y se limita al marco de Smart City Expo Santiago del Estero.</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>6. Evaluación y aceptación de la participación</h2>
            <p>La carga de información y la solicitud de participación no garantizan la aceptación automática en el Evento.</p>
            <p>La organización se reserva el derecho de:</p>
            <ul>
              <li>Aprobar, rechazar o proponer modificaciones en la modalidad de participación solicitada.</li>
              <li>Definir cupos, categorías, ubicaciones, formatos y condiciones específicas.</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>7. Responsabilidad</h2>
            <ul>
              <li>El Participante es el único responsable por la información y contenidos proporcionados.</li>
              <li>La organización no será responsable por daños directos o indirectos derivados del uso del perfil o de decisiones comerciales, institucionales o estratégicas tomadas a partir de la información compartida.</li>
              <li>La organización se reserva el derecho de suspender o eliminar perfiles que incumplan estos Términos y Condiciones.</li>
            </ul>
          </div>

          <div className="terms-section">
            <h2>8. Comunicaciones</h2>
            <p>Al aceptar estos Términos, el Participante autoriza a recibir comunicaciones por correo electrónico u otros medios digitales relacionadas con:</p>
            <ul>
              <li>El Evento</li>
              <li>Modalidades de participación</li>
              <li>Actividades, agenda y networking</li>
            </ul>
            <p>Podrá solicitar la baja de estas comunicaciones en cualquier momento.</p>
          </div>

          <div className="terms-section">
            <h2>9. Modificaciones</h2>
            <p>La organización podrá actualizar o modificar estos Términos y Condiciones. Las modificaciones serán publicadas y entrarán en vigencia desde su publicación.</p>
          </div>

          <div className="terms-section">
            <h2>10. Aceptación</h2>
            <p>Al marcar la casilla de aceptación y/o enviar el formulario, el Participante declara su aceptación expresa y plena de los presentes Términos y Condiciones.</p>
          </div>

        </div>
      </div>
      
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={isDeletingAccount}
      />
    </div>
  );
};

export default TermsAndConditions;
